import React, { useEffect, useMemo, useState } from 'react';

import { Row, Col, Card, CardBody, Label, Alert } from 'reactstrap';

import { Editor, EditorProvider } from 'react-simple-wysiwyg';

import 'react-datepicker/dist/react-datepicker.css';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import axios from 'axios';
import { api } from '../../config';
import notify from '../../utils/notify';
import { confirmSubmit } from '../../utils/confirm';
import logger from '../../utils/logger';
import { formatDateToDDMMYYYY } from '../../utils/dateformatter';

import {
  downloadParcelFreightExcel,
  collectIncompleteFreightLabels,
} from './utils/FreightCostHelpers';
import { isHpsNotAvailable } from '../Hps/utils/hpsHelpers';
import {
  orderParcelsByDisplayOrder,
  reorderParcelsByDetail,
  buildDetailOrderIndexFromGroups,
} from '../Hps/utils/parcelExportHelpers';
import CargoDeliveryTable, {
  buildCargoDeliveryExportRows,
  extractCargoList,
  mapDeliveryToRows,
  useDeliveryParcels,
  CARGO_DELIVERY_COLUMNS,
} from './component/CargoDeliveryTable';
import UploadParcelModal from './component/UploadParcelModal';
import '../../App.css';
import './FreightCostView.css';
import ActionButton from '../../Components/Common/ActionButton';
import { Skeleton } from '../../Components/Common/Skeleton';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const RequirementTab = ({
  ids,
  form,
  isProcurementLoading = false,
  _setForm,
  _dataCause,
  _handleSaveDraftProc,
  _validatedeliveryModal,
  _errors,
  _error,
  _setError,
  _setErrors,
  _setIsLoading,
  _isLoading,
  qtyDeliveryData,
  token,
}) => {
  const {
    response: parcelResponse,
    isLoading: isLoadingParcels,
    error: parcelsError,
    refresh: refreshParcels,
  } = useDeliveryParcels(ids, token);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isUploadParcelOpen, setIsUploadParcelOpen] = useState(false);

  const toggleUploadParcel = () => setIsUploadParcelOpen(open => !open);

  // ============================================================
  // Urutan Q&D (lihat Procurement/RequirementTab "Quantity &
  // Delivery Time"):
  // 1. Cargo/delivery unit → urut qtyDeliveryData (createdDate asc).
  // 2. listData + incotermFOBLocations tiap cargo → urut
  //    get-delivery-detail (= tabel Incoterm di Q&D).
  // ============================================================
  const unwrapDeliveryDetailGroups = res => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.Data)) return res.data.Data;
    return [];
  };

  const baseParcels = useMemo(() => {
    if (!parcelResponse || !qtyDeliveryData?.items?.length) return null;
    return orderParcelsByDisplayOrder(
      extractCargoList(parcelResponse),
      qtyDeliveryData.items
    );
  }, [parcelResponse, qtyDeliveryData]);

  const [detailOrdered, setDetailOrdered] = useState(null);

  useEffect(() => {
    if (!baseParcels?.length) {
      setDetailOrdered(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const indexByTermId = {};
      await Promise.all(
        baseParcels.map(async parcel => {
          try {
            const res = await axios.get(
              `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${ids}&quantityDeliveryTermId=${parcel.id}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            indexByTermId[String(parcel?.id)] = buildDetailOrderIndexFromGroups(
              unwrapDeliveryDetailGroups(res)
            );
          } catch {
            // fallback: pertahankan urutan fase 1 untuk parcel ini
          }
        })
      );
      if (cancelled) return;
      setDetailOrdered({
        base: baseParcels,
        parcels: reorderParcelsByDetail(baseParcels, indexByTermId),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [baseParcels, ids, token]);

  const orderedParcels =
    detailOrdered && detailOrdered.base === baseParcels
      ? detailOrdered.parcels
      : baseParcels;
  const tableResponse = orderedParcels ?? parcelResponse;
  const preserveOrder = Boolean(orderedParcels);

  const exportParcelFreightToExcel = () => {
    const rows = buildCargoDeliveryExportRows(tableResponse, {
      preserveOrder,
    });
    if (!rows.length) {
      notify.info('No FOB incoterm data available to export.');
      return false;
    }

    setIsExportingExcel(true);
    try {
      downloadParcelFreightExcel(
        rows,
        'Parcel_Request_Freight_Availability.xlsx',
        CARGO_DELIVERY_COLUMNS
      );
      return true;
    } catch (error) {
      notify.apiError(error, 'export parcel data to Excel');
      return false;
    } finally {
      setIsExportingExcel(false);
    }
  };

  const procurementTypeMap = {
    PelelanganKhusus: 'Pelelangan Khusus',
    PemilihanLangsung: 'Pemilihan Langsung',
    TunjukLangsung: 'Tunjuk Langsung',
  };

  const valueOrSkeleton = node =>
    isProcurementLoading ? <Skeleton width='60%' height={14} /> : node;

  const handleConfirmProcess = async () => {
    // Data parcel masih dimuat — hasil validasi belum valid, tahan dulu.
    if (isLoadingParcels) {
      notify.info('Loading parcel data, please wait.');
      return;
    }
    // Validasi Freight Lumpsum dari baris tabel yang tampil sekarang.
    const rows = mapDeliveryToRows(tableResponse, { preserveOrder });
    if (!rows.length) {
      notify.warning('No parcel data to release.');
      return;
    }
    const missing = collectIncompleteFreightLabels(rows);
    if (missing.length) {
      notify.warning(
        <div>
          Please complete :
          {missing.map(label => (
            <div key={label}>- {label}</div>
          ))}
        </div>
      );
      return;
    }
    const confirmed = await confirmSubmit('freight cost', undefined, {
      title: 'Release freight cost?',
      message:
        'The freight cost data will be released for processing. This cannot be undone.',
      confirmLabel: 'Release freight cost',
    });
    if (!confirmed) return;
    await handleSaveReleasedHps(ids);
  };

  const handleSaveReleasedHps = async releaseIds => {
    try {
      const response = await axios.put(
        `${api.API_URL}/procurement/freight-cost-submit/${releaseIds}`,
        null,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      logger.log('Response:', response.data);
    } catch (error) {
      logger.error('Error submit procurement:', error.message);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <div>
          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <div
                    color='dark'
                    className='d-flex align-items-center mb-2  '
                  >
                    <div className='flex-grow-1'>
                      <h5 className='mb-1'>
                        <i className='ri-file-list-3-line me-2'></i>
                        Request Freight Availability
                      </h5>
                      <p className='text-muted mb-2 fs-12 '>
                        Bisa jadi Kalimat tambahan untuk membantu section
                        mengenai apa ini
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Freight Requst Number :
                    </Label>
                    <p className='form-label mb-2 fs-12 '>
                      {valueOrSkeleton(form?.freightRequestNumber)}
                    </p>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Freight Request Date :
                    </Label>
                    <p className='form-label mb-2 fs-12 '>
                      {valueOrSkeleton(
                        form?.freightRequestDate
                          ? formatDateToDDMMYYYY(form.freightRequestDate)
                          : null
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <div
                    color='dark'
                    className='d-flex align-items-center mb-2  '
                  >
                    <div className='flex-grow-1'>
                      <h5 className='mb-1'>
                        <i className='ri-file-list-3-line me-2'></i>
                        Procurement Information
                      </h5>
                      <p className='text-muted mb-2 fs-12 '>
                        Bisa jadi Kalimat tambahan untuk membantu section
                        mengenai apa ini
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Procurement Type :
                    </Label>
                    <p className='form-label mb-2 fs-12 '>
                      {valueOrSkeleton(form?.procurementTypeHeaderLabel)}
                    </p>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Title Procurement :
                    </Label>
                    <p className='form-label mb-2 fs-12 '>
                      {valueOrSkeleton(form?.titleProcurement)}
                    </p>
                  </div>
                </Col>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Proc Type:
                    </Label>
                    <p className='form-label mb-2 fs-12 '>
                      {valueOrSkeleton(
                        procurementTypeMap[form.procurementType] || '-'
                      )}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col sm={12}>
                  <Card>
                    <p
                      id='req-sec-product'
                      className=' mt-2 form-label mb-1 fs-13  '
                    >
                      Description
                    </p>

                    <EditorProvider>
                      <Editor
                        id='productSpecification'
                        name='productSpecification'
                        value={form.productSpecification || ''}

                        style={{ height: '200px', overflowY: 'auto' }}
                        className={
                          form.description === false
                            ? 'form-control'
                            : 'form-control bg-light border-0'
                        }
                      ></Editor>
                    </EditorProvider>
                  </Card>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                {form.statusHPS2 != null &&
                  isHpsNotAvailable(form.statusHPS2) && (
                    <Col lg={12}>
                      <Alert
                        color='info'
                        className='d-flex align-items-start gap-2 mt-2'
                      >
                        <i className='ri-error-warning-line flex-shrink-0 mt-1'></i>
                        <span className='small'>
                          The detailed procurement plan information below can be
                          downloaded in Excel format to be used as an attachment
                          in the submission of the Freight Cost and Availability
                          Request Memorandum..
                        </span>
                      </Alert>
                    </Col>
                  )}
              </Row>

              <Row>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <div>
                    <h5 className='fw-semibold mb-1'>Parcel</h5>
                    <p className='text-muted small mb-0'>
                      Delivery terms mapped from procurement planning.
                    </p>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <ActionButton
                      variant='secondary'
                      size='sm'
                      icon='ri-file-excel-2-line'
                      disabled={isLoadingParcels}
                      loading={isExportingExcel}
                      onClick={() => exportParcelFreightToExcel()}
                    >
                      Download Parcel
                    </ActionButton>
                    {form.freightCostStatus === false && (
                      <ActionButton
                        variant='primary'
                        size='sm'
                        icon='ri-upload-2-line'
                        onClick={toggleUploadParcel}
                      >
                        Upload Parcel
                      </ActionButton>
                    )}
                  </div>
                </div>

                <CargoDeliveryTable
                  response={tableResponse}
                  loading={isLoadingParcels}
                  error={parcelsError}
                  preserveOrder={preserveOrder}
                />

                <UploadParcelModal
                  isOpen={isUploadParcelOpen}
                  toggle={toggleUploadParcel}
                  procurementId={ids}
                  token={token}
                  onUploaded={refreshParcels}
                />
              </Row>
            </CardBody>
          </Card>
        </div>

        <div className='d-flex align-items-start gap-3 mb-4'>
          {form.freightCostStatus === false && (
            <ActionButton
              variant='primary'
              icon='ri-login-box-line'
              className='ms-auto'
              disabled={isLoadingParcels}
              onClick={handleConfirmProcess}
            >
              Save & Released
            </ActionButton>
          )}
        </div>
      </Row>
    </React.Fragment>
  );
};

export default RequirementTab;
